/**
 * OCR Demo 项目常量定义
 * 集中管理所有硬编码常量，便于维护和修改
 */

// ============ API 相关常量 ============

/** API 基础 URL */
export const API_BASE_URL = 'https://aistudio.cloud.tencent.com/';

/** 默认结果获取 URL */
export const DEFAULT_RESULT_URL = `${API_BASE_URL}demo-user/capi/getResult/`;

/** API 路径常量 */
export const API_PATHS = {
  /** 示例图片结果获取路径 */
  DEMO_RESULT_BY_DEFAULT_IMAGE: 'demo-common/democase/getDemoResultByDefaultImage',
  /** 用户结果获取路径 */
  GET_RESULT: 'demo-user/capi/getResult/',
  /** 产品配置获取路径 */
  GET_PRODUCT_CONFIG: 'demo-common/getProductConfig',
};

/** HTTP 请求超时时间（毫秒） */
export const HTTP_TIMEOUT = 50000;

/** 最大轮询次数 */
export const MAX_POLL_COUNT = 10;

/** 默认轮询间隔（毫秒） */
export const POLL_INTERVAL = 1000;

// ============ 错误处理常量 ============

/** 不需要弹窗提示的错误代码列表 */
export const NOT_ALERT_CODE_LIST = [
  'FailedOperation.OcrNotReadyError',
  'FailedOperation.TaskClosedError',
  'FailedOperation.TaskCommittedError',
];

/** 错误代码定义 */
export const ERROR_CODES = {
  NO_PERMISSION: -1004,
  NEED_LOGIN: -1003,
  SENSITIVE_IMAGE: -2010,
  AUTH_FAILURE: 'AuthFailure.SignatureFailure',
  UNKNOWN_ERROR: -1,
};

// ============ HTTP 状态码常量 ============

/** HTTP 状态码常量 */
export const HTTP_STATUS_CODES = {
  /** 请求成功 */
  OK: 200,
  /** 错误的请求 */
  BAD_REQUEST: 400,
  /** 未授权 */
  UNAUTHORIZED: 401,
  /** 禁止访问 */
  FORBIDDEN: 403,
  /** 资源未找到 */
  NOT_FOUND: 404,
  /** 服务器内部错误 */
  INTERNAL_SERVER_ERROR: 500,
};

/** API 状态码常量 */
export const API_STATUS_CODES = {
  /** API 调用成功 */
  SUCCESS: 0,
  /** 请求参数错误 */
  PARAMETER_ERROR: -1,
  /** 网络连接异常 */
  NETWORK_ERROR: -1001,
  /** 服务器内部错误 */
  SERVER_ERROR: -1002,
  /** 需要登录 */
  NEED_LOGIN: -1003,
  /** 权限不足 */
  NO_PERMISSION: -1004,
  /** 敏感图片 */
  SENSITIVE_IMAGE: -2010,
};

// ============ 文件上传相关常量 ============

/** 默认支持的文件格式列表 */
export const DEFAULT_ACCEPT_LIST = ['jpg', 'jpeg', 'png'];

/** 默认最大文件大小（MB） */
export const DEFAULT_MAX_FILE_SIZE = 10;

/** 默认图片前缀 */
export const DEFAULT_IMAGE_PREFIX = 'Image';

/** Office 文档扩展名列表 */
export const OFFICE_FILE_EXTENSIONS = ['docx', 'doc', 'xlsx', 'xls'];

// ============ MIME 类型常量 ============

/** MIME 类型常量定义 */
export const MIME_TYPES = {
  /** Excel 文件 MIME 类型 */
  EXCEL: 'application/vnd.ms-excel',
  /** ZIP 压缩包文件 MIME 类型 */
  ZIP: 'data:application/zip;base64',
  /** Markdown 文件 MIME 类型 */
  MARKDOWN: 'data:text/markdown;base64',
  /** BMP 图片文件 MIME 类型 */
  BMP: 'data:image/bmp;base64',
  /** 八位字节流 MIME 类型 */
  OCTET_STREAM: 'data:application/octet-stream;base64',
};

// ============ 文件扩展名常量 ============

/** 文件扩展名常量定义 */
export const FILE_EXTENSIONS = {
  /** Excel 文件扩展名 */
  EXCEL: '.xlsx',
  /** Markdown 文件扩展名 */
  MARKDOWN: '.md',
  /** ZIP 压缩包文件扩展名 */
  ZIP: '.zip',
};

// ============ 异步任务相关常量 ============

/** 最大结果等待时间（秒 * 5 = 5分钟） */
export const MAX_RESULT_TIMES = 60 * 5;

/** 最大结果间隔（毫秒） */
export const MAX_RESULT_GAP = 2000;

// ============ 正则表达式常量 ============

/** 正则表达式集合 */
export const REGEX = {
  /** URL 正则表达式 */
  URL: /(https?|ftp|file):\/\/[-A-Za-z0-9+&@#/%?=~_|!:,.;]+[-A-Za-z0-9+&@#/%=~_|]/,
  
  /** 中英文逗号分割正则 */
  COMMA: /[,，]/,
  
  /** 空白字符正则 */
  WHITESPACE: /^\s*$/,
  
  /** 数字正则（支持小数） */
  NUMBER: /^[0-9]+\.{0,1}[0-9]{0,2}$/,
};

// ============ UI 相关常量 ============

/** 消息提示持续时间（毫秒） */
export const MESSAGE_DURATION = 5000;

/** 默认缩放比例 */
export const DEFAULT_ZOOM_SCALE = 50;

/** 缩放步长 */
export const ZOOM_STEP = 10;

/** 最小缩放比例 */
export const ZOOM_MIN = 20;

/** 最大缩放比例 */
export const ZOOM_MAX = 180;

/** PDF 页面间隙 */
export const PDF_GAP = 8;

/** 滚动防抖延迟 */
export const SCROLL_DEBOUNCE_DELAY = 100;

// ============ 菜单和导航相关常量 ============

/** 最大最近访问菜单数量 */
export const MAX_RECENT_MENU_COUNT = 4;

/** API 文档 URL */
export const API_DOC_URL = 'https://cloud.tencent.com/document/product/866/33515';

/** 控制台 URL */
export const CONSOLE_URL = 'https://console.cloud.tencent.com/ocr/overview';

/** OCR Demo 首页 URL */
export const OCR_DEMO_URL = 'https://ocrdemo.cloud.tencent.com/';

// ============ 消息文本常量 ============

/** 默认消息文本 */
export const DEFAULT_MESSAGES = {
  /** 网络异常提示 */
  NETWORK_ERROR: '网络异常，请重试',
  
  /** 识别失败提示 */
  RECOGNIZE_FAILED: '识别失败',
  
  /** 复制成功提示 */
  COPY_SUCCESS: '复制成功',
  
  /** 深度思考中提示 */
  DEEP_SEEKING: '大模型智能抽取中...',
  
  /** 识别中提示 */
  RESULTING: '识别中...',
  
  /** 空结果提示 */
  EMPTY_RESULT: '暂无结果',
  
  /** URL 地址错误提示 */
  INVALID_URL: '请输入正确的url地址',
  
  /** 文件格式错误提示 */
  INVALID_FILE_FORMAT: '文件格式不正确',
  
  /** 文件大小超限提示 */
  FILE_TOO_LARGE: '文件大小不能超过',
  
  /** 任务图片识别失败提示 */
  TASK_IMAGE_RECOGNITION_FAILED: '任务图片识别失败',
  
  /** 文件选择提示 */
  SELECT_FILE: '请选择文件',
  
  /** 服务器错误消息 */
  SERVER_ERROR: '服务器访问出错。',
  
  /** 敏感图片错误消息 */
  SENSITIVE_IMAGE_ERROR: '图片包含敏感信息，请更换图片。',
  
  /** 权限错误消息 */
  PERMISSION_ERROR: 'error! 您无权限访问',

  FILE_TOO_LARGE_BASE641: '图片经Base64编码后超过',
  FILE_TOO_LARGE_BASE642: '限制，请将图片压缩到约7.5M以内。',
};

// ============ 错误消息常量 ============

/** 复制操作相关错误消息 */
export const COPY_ERROR_MESSAGES = {
  /** 降级方案复制失败 */
  FALLBACK_COPY_FAILED: '降级方案复制失败',
  /** 降级方案复制出错 */
  FALLBACK_COPY_ERROR: '降级方案复制出错:',
  /** 现代API复制失败 */
  MODERN_COPY_FAILED: '现代API复制失败，使用降级方案:',
  /** 通用复制失败 */
  GENERIC_COPY_FAILED: '复制失败:',
  /** 通用错误消息 */
  GENERIC_ERROR: '错误',
};

// ============ UI 文本常量 ============

/** UI 文本常量定义 */
export const UI_TEXTS = {
  /** 正确答案状态 */
  CORRECT: '正确',
  /** 错误答案状态 */
  WRONG: '错误',
  /** 题目统计前缀 */
  TOTAL_QUESTIONS: '总共解析',
  /** 题目统计单位 */
  QUESTIONS_UNIT: '道大题',
};

// ============ 颜色常量 ============

/** 颜色常量定义 */
export const COLORS = {
  /** 主色调 - 蓝色 */
  PRIMARY: '#4A70FF',
  /** 成功色调 - 绿色 */
  SUCCESS: '#0FB87F',
  /** 警告色调 - 橙色 */
  WARNING: '#FF8345',
  /** 浅蓝色调 - 用于矩形边框 */
  LIGHT_BLUE: '#AACFFF',
  /** 透明 */
  TRANSPARENT: 'transparent',
  /** 半透明蓝色 - 用于激活状态 */
  ACTIVE_BLUE: 'rgba(0, 110, 255, 0.12)',
  /** 文字主色调 */
  TEXT_PRIMARY: '#32394B',
  /** 背景色调 - 浅灰蓝 */
  BG_LIGHT_BLUE: '#F1F6FF',
  /** 成功背景色调 - 浅绿 */
  BG_SUCCESS: '#E6F8EF',
};

// ============ 数值常量 ============

/** 数值常量定义 */
export const NUMERICAL_CONSTANTS = {
  /** 默认滚动距离 */
  DEFAULT_SCROLL_DISTANCE: 200,
  /** 默认页码 */
  DEFAULT_PAGE_NUMBER: 1,
  /** 默认文件页数 */
  DEFAULT_FILE_PAGE_NUMBER: 1,
  /** HTTP 请求成功状态码 */
  HTTP_SUCCESS_CODE: 200,
  /** API 请求成功状态码 */
  API_SUCCESS_CODE: 0,
};

// ============ 响应标签配置 ============

/** 默认响应标签配置 */
export const DEFAULT_RESPONSE_TABS = [
  { id: 'Result', label: '识别结果', type: 'Result' },
  { id: 'Request', label: 'Request', type: 'Request' },
  { id: 'Response', label: 'Response', type: 'Response' },
];

// ============ 矩形框样式常量 ============

/** 默认矩形框样式 */
export const DEFAULT_RECT_STYLE = {
  visible: false,
  cantSelect: false,
  fill: 'transparent',
  activeFill: 'rgba(0, 110, 255, 0.12)',
  stroke: '#AACFFF',
  activeStoke: '#4A70FF',
};

/** 矩形框标题样式 */
export const RECT_TITLE_STYLES = {
  primary: {
    activeColor: '#4A70FF',
    color: '#4A70FF',
    bgColor: '#F1F6FF',
  },
  success: {
    activeColor: '#0FB87F',
    color: '#E6F8EF',
    bgColor: '#E6F8EF',
  },
  warning: {
    activeColor: '#FF8345',
    color: '#F1F6FF',
    bgColor: '#FFF8F0',
  },
};

// ============ COS 资源配置 ============

/** COS 资源 URL */
export const COS_URL = 'https://ocr-demo-1254418846.cos.ap-guangzhou.myqcloud.com/';

// ============ 缓存配置常量 ============

// 缓存配置已迁移至 src/cache/indexedDbCache.js 中的 DEFAULT_CONFIG

// ============ 教育 Agent 相关常量 ============

/** 教育 Agent 默认图片标识（用于判断是否为示例图） */
export const DEFAULT_EDU_AGENT_IMG_PREFIX = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAA5wAAABWCAYAAACjKZme';